import { queryOptions } from '@tanstack/react-query';
import { fetchJob, fetchJobs } from '@/server/jobs.functions';
import { fetchProfile } from '@/server/profile.functions';
import {
  fetchDaemonStatus,
  fetchLastScan,
  fetchStats,
} from '@/server/stats.functions';
import type { Job } from '@/types/job.type';

interface Status {
  byStatus: Record<string, number>;
  skipped?: number;
  total: number;
}

export interface Stats {
  applications: Status;
  daily: {
    count: number;
    date: string;
  }[];
  jobs: Status;
}

export interface DaemonStatus {
  lastHeartbeatAt: string | null;
  mode: 'peak' | 'offpeak' | 'weekend' | null;
  nextScanAt: string | null;
  pid: number | null;
  running: boolean;
  schedule: { peakMin: number; offpeakMin: number } | null;
  stale: boolean;
  startedAt: string | null;
  stoppedAt: string | null;
  uptimeMs: number | null;
}

export interface ScanRunPerSource {
  errors: string[];
  new: number;
  total: number;
}

export interface LastScanResult {
  durationMs: number | null;
  errorsCount: number;
  finishedAt: string | null;
  id: number;
  jobsDeduped: number;
  jobsNew: number;
  perSource: Record<string, ScanRunPerSource> | null;
  scoredAttempted: number;
  scoredSucceeded: number;
  startedAt: string;
  tailoredEligible: number;
  tailoredSucceeded: number;
  triggerSource: 'daemon' | 'manual';
}

interface JobsResponse {
  jobs: Job[];
  total: number;
}

export const throwIfNotOk = async (res: Response) => {
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    const message =
      (body && typeof body === 'object' && 'error' in body && body.error)
      || res.statusText
      || res.status;

    throw new Error(String(message));
  }
};

export const fetchJson = async <T>(url: string, init?: RequestInit) => {
  const res = await fetch(url, init);

  await throwIfNotOk(res);

  return res.json() as Promise<T>;
};

const fetchAuthState = async () => {
  const res = await fetchJson<{ authenticated: boolean }>('/api/session');

  return res.authenticated;
};

export const authQueryOptions = () =>
  queryOptions({
    queryFn: fetchAuthState,
    queryKey: ['auth'],
    retry: false,
  });

export const statsQueryOptions = () =>
  queryOptions({
    queryFn: () => fetchStats(),
    queryKey: ['stats'],
  });

export const daemonStatusQueryOptions = () =>
  queryOptions({
    queryFn: () => fetchDaemonStatus(),
    queryKey: ['daemon-status'],
    refetchInterval: 10_000,
  });

export const lastScanQueryOptions = () =>
  queryOptions({
    queryFn: () => fetchLastScan(),
    queryKey: ['last-scan'],
    refetchInterval: 30_000,
  });

export const jobsQueryOptions = (filters: {
  page: number;
  pageSize?: number;
  status: string | null;
}) =>
  queryOptions({
    queryFn: () =>
      fetchJobs({
        data: {
          page: filters.page,
          pageSize: filters.pageSize ?? 50,
          status: filters.status ?? undefined,
        },
      }) as Promise<JobsResponse>,
    queryKey: ['jobs', filters],
    retry: false,
  });

export const jobDetailQueryOptions = (id: number | null) =>
  queryOptions({
    enabled: id != null,
    queryFn: () => {
      if (id == null) {
        throw new Error('Job id is required');
      }

      return fetchJob({ data: { id } }) as Promise<Job>;
    },
    queryKey: ['job', id],
  });

export const processSseLine = <T>(line: string, current: T): T => {
  if (!line.startsWith('data: ')) {
    return current;
  }

  try {
    const parsed = JSON.parse(line.slice(6)) as {
      error?: string;
      result?: T;
      warning?: string;
    };

    if (parsed.error) {
      throw new Error(parsed.error);
    }

    if (parsed.warning) {
      const existing = (current as Record<string, unknown>).warnings as
        | string[]
        | undefined;

      return {
        ...(current as Record<string, unknown>),
        warnings: [...(existing ?? []), parsed.warning],
      } as T;
    }

    // Merge (rather than replace) so warnings accumulated earlier in the
    // stream survive the final `result` event.
    return parsed.result !== undefined
      ? ({
          ...(current as Record<string, unknown>),
          ...(parsed.result as Record<string, unknown>),
        } as T)
      : current;
  } catch (parseError) {
    if (parseError instanceof SyntaxError) {
      return current;
    }

    throw parseError;
  }
};

export const consumeSseStream = async <T extends Record<string, unknown>>(
  res: Response,
  initial: T,
): Promise<T> => {
  const reader = res.body?.getReader();

  if (!reader) {
    throw new Error('No response body');
  }

  let lastResult = initial;
  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();

    if (done) {
      break;
    }

    buffer += decoder.decode(value, { stream: true });

    // Split on newlines but keep the trailing (possibly incomplete) line in
    // the buffer — an event can be split across TCP chunks, and parsing a
    // partial `data:` line would silently drop it.
    const lines = buffer.split('\n');
    buffer = lines.pop() ?? '';

    for (const line of lines) {
      lastResult = processSseLine(line, lastResult);
    }
  }

  // Flush any final line left in the buffer (a last event with no trailing
  // newline, e.g. the closing `result`).
  if (buffer.length > 0) {
    lastResult = processSseLine(buffer, lastResult);
  }

  return lastResult;
};

export interface Profile {
  application_answers: string | null;
  disable_weekend_scans: number | null;
  disabled_scrapers: string | null;
  discord_webhook: string | null;
  experience_years: number | null;
  id: number;
  linkedin_cookie: string | null;
  location_preference: string | null;
  match_threshold: number | null;
  max_score_per_cycle: number | null;
  max_tailor_per_cycle: number | null;
  min_salary: number | null;
  name: string;
  offpeak_interval_min: number | null;
  peak_end_hour: number | null;
  peak_interval_min: number | null;
  peak_start_hour: number | null;
  preferences: string | null;
  requirements: string | null;
  resume_file_name: string | null;
  resume_file_path: string | null;
  resume_md: string | null;
  resume_raw: string | null;
  scoring_model: string | null;
  skills: string;
  summary: string | null;
  tailoring_model: string | null;
  target_roles: string;
  timezone: string | null;
}

export type ApplyAttemptStatus =
  | 'queued'
  | 'running'
  | 'needs_review'
  | 'submitted'
  | 'error';

export interface ApplyAttemptFilledField {
  label: string;
  value: string;
}

export interface ApplyAttemptUnfilledField {
  label: string;
  reason: string;
}

/*
  The shape of the latest apply attempt as returned by
  /api/applications/$id/apply-attempt (i.e. getLatestApplyAttempt with the
  filled_fields/unfilled_fields columns JSON-parsed into arrays).
*/
export interface ApplyAttempt {
  application_id: number;
  apply_url: string | null;
  ats_vendor: string | null;
  error_message: string | null;
  filled_fields: ApplyAttemptFilledField[] | null;
  finished_at: string | null;
  id: number;
  kernel_session_id: string | null;
  live_view_url: string | null;
  screenshot_path: string | null;
  started_at: string;
  status: ApplyAttemptStatus;
  unfilled_fields: ApplyAttemptUnfilledField[] | null;
}

export const profileQueryOptions = () =>
  queryOptions({
    queryFn: () => fetchProfile() as Promise<{ profile: Profile | null }>,
    queryKey: ['profile'],
    retry: false,
  });
